import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Coins, Heart, Star, Sparkles, ShoppingBag,
  ArrowRightLeft, ChevronRight, Utensils, Plus, X, Check, Send, Package,
  XCircle, Search, Globe, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const GROWTH_LABELS = { baby: 'Baby', kid: 'Kid', teen: 'Teen', super: 'Super Pet' };
const GROWTH_COLORS = { baby: 'bg-blue-400', kid: 'bg-green-400', teen: 'bg-purple-400', super: 'bg-amber-400' };
const STAGE_ORDER = ['baby', 'kid', 'teen', 'super'];
const PET_CATALOG_INFO = {
  dog: { cost: 50 }, cat: { cost: 60 }, bunny: { cost: 70 }, turtle: { cost: 80 }, hamster: { cost: 90 }, chinchilla: { cost: 100 },
  fox: { cost: 200 }, owl: { cost: 220 }, penguin: { cost: 250 }, panda: { cost: 280 }, dolphin: { cost: 300 },
  crystal_cub: { cost: 500 }, galaxy_octopus: { cost: 600 }, golden_dino: { cost: 750 }, lightning_dragon: { cost: 1000 },
};

// ──── Sub-Components ────

const PetMiniCard = ({ pet, onClick, isSelected }) => (
  <button
    data-testid={`pet-mini-${pet.pet_id}`}
    onClick={() => onClick(pet)}
    className={`relative p-3 rounded-2xl border-2 transition-all text-center hover:scale-105 ${
      isSelected ? 'border-purple-500 bg-purple-50 shadow-lg scale-105' : 'border-slate-200 bg-white hover:border-purple-300'
    }`}
  >
    <div className="text-4xl mb-1">{pet.emoji}</div>
    {pet.equipped_accessories?.length > 0 && (
      <div className="absolute -top-1 -right-1 flex">
        {pet.equipped_accessories.slice(0, 3).map((accId, i) => (
          <span key={accId} className="text-sm" style={{ marginLeft: i > 0 ? '-4px' : 0 }}>
            {accId.includes('hat') ? '🎩' : accId.includes('glass') ? '👓' : accId.includes('cape') ? '🦸' : accId.includes('toy') ? '🎾' : '✨'}
          </span>
        ))}
      </div>
    )}
    <p className="font-bold text-slate-800 text-sm truncate">{pet.name}</p>
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold text-white ${GROWTH_COLORS[pet.growth_stage]}`}>
      {GROWTH_LABELS[pet.growth_stage]}
    </span>
    <div className="flex items-center justify-center gap-1 mt-1">
      <Heart className="w-3 h-3 text-pink-500" fill={pet.happiness > 50 ? '#ec4899' : 'none'} />
      <span className="text-xs text-slate-500">{pet.happiness}%</span>
    </div>
  </button>
);

const PetDetail = ({ pet, foodInventory, foodTypes, onFeed, onClose, ownedAccessories, accessoriesStore, onAccessorize }) => {
  const [feedingFood, setFeedingFood] = useState(null);
  const stageIdx = STAGE_ORDER.indexOf(pet.growth_stage);
  const nextStage = stageIdx < 3 ? STAGE_ORDER[stageIdx + 1] : null;
  const progressToNext = nextStage ? Math.min(100, (pet.growth_progress / (7 * (stageIdx + 1))) * 100) : 100;

  const handleFeed = async (foodKey) => {
    setFeedingFood(foodKey);
    await onFeed(pet.pet_id, foodKey);
    setFeedingFood(null);
  };

  const availableFoods = Object.entries(foodTypes || {}).filter(([key]) => (foodInventory?.[key] || 0) > 0);

  return (
    <div data-testid="pet-detail" className="bg-white rounded-3xl border-2 border-purple-200 shadow-xl overflow-hidden animate-slide-up">
      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-5 text-white relative">
        <button onClick={onClose} className="absolute top-3 right-3 p-1 bg-white/20 rounded-full hover:bg-white/30">
          <X className="w-5 h-5" />
        </button>
        <div className="text-center">
          <div className="text-6xl mb-2">{pet.emoji}</div>
          <h3 className="text-2xl font-black font-fredoka">{pet.name}</h3>
          <div className="flex items-center justify-center gap-3 mt-2 text-sm">
            <span className={`px-3 py-1 rounded-full font-bold ${GROWTH_COLORS[pet.growth_stage]} text-white`}>
              {GROWTH_LABELS[pet.growth_stage]}
            </span>
            {pet.rarity === 'rare' && <span className="px-3 py-1 rounded-full bg-amber-400 text-amber-900 font-bold">Rare</span>}
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-pink-50 rounded-xl p-3 text-center">
            <Heart className="w-5 h-5 text-pink-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-pink-600">{pet.happiness}%</p>
            <p className="text-xs text-pink-400">Happiness</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-3 text-center">
            <Star className="w-5 h-5 text-purple-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-purple-600">{pet.growth_progress}</p>
            <p className="text-xs text-purple-400">Growth XP</p>
          </div>
        </div>

        {/* Growth Progress */}
        {nextStage && (
          <div>
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>{GROWTH_LABELS[pet.growth_stage]}</span>
              <span>{GROWTH_LABELS[nextStage]}</span>
            </div>
            <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-purple-400 to-indigo-500 rounded-full transition-all" style={{ width: `${progressToNext}%` }} />
            </div>
            <p className="text-xs text-slate-400 mt-1 text-center">
              {Math.max(0, 7 * (stageIdx + 1) - pet.growth_progress)} more XP to evolve
            </p>
          </div>
        )}
        {!nextStage && (
          <div className="text-center bg-amber-50 rounded-xl p-3 border border-amber-200">
            <Sparkles className="w-5 h-5 text-amber-500 mx-auto" />
            <p className="text-amber-700 font-bold text-sm">Max Level Reached!</p>
          </div>
        )}

        {/* Equipped Accessories */}
        {pet.equipped_accessories?.length > 0 && (
          <div>
            <p className="text-sm font-bold text-slate-700 mb-2">Equipped</p>
            <div className="flex flex-wrap gap-2">
              {pet.equipped_accessories.map(accId => {
                const allAcc = Object.values(accessoriesStore || {}).flat();
                const acc = allAcc.find(a => a.id === accId);
                return acc ? (
                  <span key={accId} className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium">
                    {acc.emoji} {acc.name}
                  </span>
                ) : null;
              })}
            </div>
          </div>
        )}

        {/* Feed Section */}
        <div>
          <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1">
            <Utensils className="w-4 h-4" /> Feed {pet.name}
          </p>
          {availableFoods.length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {availableFoods.map(([key, info]) => (
                <button
                  key={key}
                  data-testid={`feed-${key}`}
                  onClick={() => handleFeed(key)}
                  disabled={feedingFood === key}
                  className="p-2 bg-slate-50 rounded-xl border border-slate-200 hover:border-green-400 hover:bg-green-50 transition-all text-center"
                >
                  <span className="text-2xl block">{info.emoji}</span>
                  <span className="text-[10px] font-bold text-slate-600 block">{info.name}</span>
                  <span className="text-[10px] text-slate-400">x{foodInventory[key]}</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-2">No food yet! Complete quizzes & riddles to earn food.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const AdoptionCard = ({ pet, onAdopt, userCoins }) => {
  const [naming, setNaming] = useState(false);
  const [petName, setPetName] = useState(pet.name);
  const canAfford = userCoins >= pet.cost;

  return (
    <div data-testid={`adopt-card-${pet.type}`} className={`bg-white rounded-2xl border-2 overflow-hidden transition-all ${
      pet.owned ? 'border-green-400 opacity-60' : canAfford ? 'border-slate-200 hover:border-purple-400 hover:shadow-lg' : 'border-slate-200 opacity-50'
    } ${pet.rarity === 'rare' ? 'ring-2 ring-amber-400 ring-offset-2 shadow-[0_0_15px_rgba(251,191,36,0.4)]' : ''} ${pet.rarity === 'uncommon' ? 'ring-2 ring-teal-400 ring-offset-1 shadow-[0_0_10px_rgba(20,184,166,0.3)]' : ''}`}>
      {pet.rarity === 'rare' && (
        <div className="bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 text-center py-1 animate-pulse">
          <span className="text-xs font-bold text-amber-900">Rare Pet {pet.coin_bonus ? `(+${pet.coin_bonus} bonus coins)` : ''}</span>
        </div>
      )}
      {pet.rarity === 'uncommon' && (
        <div className="bg-gradient-to-r from-teal-400 via-emerald-300 to-teal-400 text-center py-1">
          <span className="text-xs font-bold text-teal-900">Uncommon {pet.coin_bonus ? `(+${pet.coin_bonus} bonus coins)` : ''}</span>
        </div>
      )}
      <div className="p-4 text-center">
        <div className="text-5xl mb-2">{pet.emoji}</div>
        <h3 className="font-bold text-slate-900">{pet.name}</h3>
        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{pet.description}</p>
        <div className="flex items-center justify-center gap-1 mt-2 font-bold text-amber-600">
          <Coins className="w-4 h-4" /> {pet.cost}
        </div>

        {pet.owned ? (
          <div className="mt-3 py-2 bg-green-100 rounded-xl text-green-700 font-bold text-sm flex items-center justify-center gap-1">
            <Check className="w-4 h-4" /> Adopted
          </div>
        ) : naming ? (
          <div className="mt-3 space-y-2">
            <input
              data-testid={`name-input-${pet.type}`}
              value={petName}
              onChange={e => setPetName(e.target.value)}
              maxLength={20}
              placeholder="Name your pet..."
              className="w-full px-3 py-2 border-2 border-purple-300 rounded-xl text-sm text-center focus:outline-none focus:border-purple-500"
            />
            <div className="flex gap-2">
              <button onClick={() => setNaming(false)} className="flex-1 py-2 bg-slate-100 rounded-xl text-slate-600 text-sm font-bold">Cancel</button>
              <button
                data-testid={`confirm-adopt-${pet.type}`}
                onClick={() => { onAdopt(pet.type, petName); setNaming(false); }}
                className="flex-1 py-2 bg-gradient-to-r from-green-400 to-emerald-500 text-white rounded-xl text-sm font-bold"
              >Adopt!</button>
            </div>
          </div>
        ) : (
          <button
            data-testid={`adopt-btn-${pet.type}`}
            onClick={() => setNaming(true)}
            disabled={!canAfford}
            className={`mt-3 w-full py-2 rounded-xl font-bold text-sm transition-all ${
              canAfford ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:shadow-md active:scale-95' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >{canAfford ? 'Adopt' : `Need ${pet.cost} coins`}</button>
        )}
      </div>
    </div>
  );
};

const AccessoryItem = ({ item, onBuy, selectedPetId, userCoins }) => {
  const canAfford = item.owned || userCoins >= item.cost;
  return (
    <button
      data-testid={`acc-${item.id}`}
      onClick={() => onBuy(item.id)}
      disabled={!canAfford || !selectedPetId}
      className={`p-3 rounded-xl border-2 text-center transition-all ${
        item.owned ? 'border-green-300 bg-green-50 hover:bg-green-100' : canAfford ? 'border-slate-200 bg-white hover:border-purple-300' : 'border-slate-200 opacity-50'
      }`}
    >
      <span className="text-2xl block">{item.emoji}</span>
      <span className="text-xs font-bold text-slate-700 block mt-1">{item.name}</span>
      {item.owned ? (
        <span className="text-[10px] text-green-600 font-bold">Owned - Tap to equip</span>
      ) : (
        <span className="text-[10px] text-amber-600 font-bold flex items-center justify-center gap-0.5"><Coins className="w-3 h-3" />{item.cost}</span>
      )}
    </button>
  );
};

// ──── Confirm Modal (replaces window.confirm) ────
const ConfirmModal = ({ title, message, emoji, onConfirm, onCancel, confirmText = 'Yes', cancelText = 'No' }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fade-in">
    <div className="bg-white rounded-3xl p-6 w-full max-w-xs text-center animate-pop">
      <div className="text-5xl mb-3">{emoji}</div>
      <h3 className="font-bold text-lg text-slate-900 mb-1">{title}</h3>
      <p className="text-slate-500 text-sm mb-5">{message}</p>
      <div className="flex gap-3">
        <button onClick={onCancel} className="flex-1 py-3 bg-slate-100 rounded-2xl font-bold text-slate-700 text-sm">{cancelText}</button>
        <button data-testid="confirm-action-btn" onClick={onConfirm} className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-2xl font-bold text-sm">{confirmText}</button>
      </div>
    </div>
  </div>
);

// ──── Main Page ────

const TABS = [
  { id: 'my_pets', label: 'My Pets', icon: '🐾' },
  { id: 'adopt', label: 'Adopt', icon: '🏠' },
  { id: 'shop', label: 'Shop', icon: '🎒' },
  { id: 'trade', label: 'Trade', icon: '🔄' },
  { id: 'trading_post', label: 'Trading Post', icon: '🌍' },
];

const PetWorldPage = () => {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my_pets');
  const [selectedPet, setSelectedPet] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [tradeData, setTradeData] = useState(null);
  const [tradePet, setTradePet] = useState(null);
  const [tradeUsername, setTradeUsername] = useState('');
  const [wantPetType, setWantPetType] = useState(null);
  const [swapPet, setSwapPet] = useState(null);
  const [swapTarget, setSwapTarget] = useState(null);
  const [tradeFeed, setTradeFeed] = useState(null);
  const [feedSearch, setFeedSearch] = useState('');
  const [offerTradeId, setOfferTradeId] = useState(null);
  const [offerPetPick, setOfferPetPick] = useState(null);
  const apiRef = useRef(api);
  useEffect(() => { apiRef.current = api; }, [api]);

  const fetchData = useCallback(async () => {
    try {
      const res = await apiRef.current.get('/pets');
      setData(res.data);
      setSelectedPet(prev => {
        if (!prev && res.data.my_pets.length > 0) return res.data.my_pets[0];
        if (prev) return res.data.my_pets.find(p => p.pet_id === prev.pet_id) || null;
        return null;
      });
    } catch (err) {
      toast.error('Could not load Pet World');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdopt = async (petType, name) => {
    try {
      const res = await apiRef.current.post('/pets/adopt', { pet_type: petType, name });
      toast.success(res.data.message);
      await refreshUser();
      await fetchData();
      setActiveTab('my_pets');
      setSelectedPet(res.data.pet);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not adopt pet');
    }
  };

  const handleFeed = async (petId, foodType) => {
    try {
      const res = await apiRef.current.post('/pets/feed', { pet_id: petId, food_type: foodType });
      if (res.data.evolved) {
        toast.success(`${selectedPet?.name} EVOLVED to ${GROWTH_LABELS[res.data.growth_stage]}!`, { duration: 4000 });
      } else {
        toast.success(res.data.message);
      }
      await refreshUser();
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not feed pet');
    }
  };

  const handleAccessorize = async (accId) => {
    if (!selectedPet) return toast.error('Select a pet first!');
    try {
      const res = await apiRef.current.post('/pets/accessorize', { pet_id: selectedPet.pet_id, accessory_id: accId });
      toast.success(res.data.message);
      await refreshUser();
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not equip accessory');
    }
  };

  const handleTrade = async (petId, tradeType) => {
    const pet = data?.my_pets?.find(p => p.pet_id === petId);
    if (!pet) return;
    const catalog = PET_CATALOG_INFO[pet.pet_type];
    const tradeValue = catalog ? Math.floor(catalog.cost * 0.6) : '?';

    if (tradeType === 'coins') {
      setConfirmAction({
        title: 'Trade for Coins?',
        message: `Trade ${pet.name} for ${tradeValue} coins? (60% of adoption cost)`,
        emoji: '🪙',
        confirmText: 'Trade',
        onConfirm: async () => {
          setConfirmAction(null);
          try {
            const res = await apiRef.current.post('/pets/trade', { pet_id: petId, trade_type: 'coins' });
            toast.success(res.data.message);
            if (selectedPet?.pet_id === petId) setSelectedPet(null);
            await refreshUser();
            await fetchData();
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Trade failed');
          }
        }
      });
    } else if (tradeType === 'random') {
      // Open visual swap picker
      setSwapPet(pet);
      setSwapTarget(null);
    }
  };

  const confirmSwap = () => {
    if (!swapPet) return;
    const rareTypes = ['crystal_cub', 'galaxy_octopus', 'golden_dino', 'lightning_dragon'];
    const uncommonTypes = ['fox', 'owl', 'penguin', 'panda', 'dolphin'];
    const isOfferingRare = swapPet.rarity === 'rare';
    const isReceivingCommon = swapTarget && !rareTypes.includes(swapTarget.type) && !uncommonTypes.includes(swapTarget.type);

    if (isOfferingRare && isReceivingCommon) {
      setConfirmAction({
        title: 'Fair Trade Warning!',
        message: `You're trading a RARE pet (${swapPet.name}) for a Common pet. Are you sure?`,
        emoji: '⚠️',
        confirmText: 'Trade Anyway',
        onConfirm: async () => {
          setConfirmAction(null);
          await executeSwap();
        }
      });
    } else {
      executeSwap();
    }
  };

  const executeSwap = async () => {
    try {
      const res = await apiRef.current.post('/pets/trade', {
        pet_id: swapPet.pet_id,
        trade_type: 'random',
        want_pet_type: swapTarget?.type || undefined,
      });
      toast.success(res.data.message);
      if (selectedPet?.pet_id === swapPet.pet_id) setSelectedPet(null);
      setSwapPet(null);
      setSwapTarget(null);
      await refreshUser();
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Swap failed');
    }
  };

  const fetchTrades = useCallback(async () => {
    try { const res = await apiRef.current.get('/pets/trades'); setTradeData(res.data); } catch (e) { /* */ }
  }, []);

  const fetchTradeFeed = useCallback(async (query = '') => {
    try {
      const res = await apiRef.current.get(`/pets/trade/feed?search=${encodeURIComponent(query)}`);
      setTradeFeed(res.data);
    } catch (e) { /* */ }
  }, []);

  useEffect(() => { if (activeTab === 'trade') { fetchTrades(); fetchData(); } }, [activeTab, fetchTrades, fetchData]);
  useEffect(() => { if (activeTab === 'trading_post') { fetchTradeFeed(); } }, [activeTab, fetchTradeFeed]);

  const handleSendFriendTrade = async () => {
    if (!tradePet) return toast.error('Select a pet to offer!');
    try {
      const res = await apiRef.current.post('/pets/trade/send', {
        pet_id: tradePet.pet_id,
        to_username: tradeUsername || undefined,
        want_pet_type: wantPetType || undefined,
      });
      toast.success(res.data.message);
      setTradePet(null);
      setTradeUsername('');
      setWantPetType(null);
      await fetchTrades();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send trade!');
    }
  };

  const handleCancelPetTrade = async (tradeId) => {
    try {
      const res = await apiRef.current.post('/pets/trade/cancel', { trade_id: tradeId });
      toast.success(res.data.message);
      await fetchTrades();
      await fetchTradeFeed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancel failed!');
    }
  };

  const handleOfferOnTrade = async (tradeId, petId) => {
    try {
      const res = await apiRef.current.post('/pets/trade/offer', { trade_id: tradeId, offer_pet_id: petId });
      toast.success(res.data.message);
      setOfferTradeId(null);
      setOfferPetPick(null);
      await fetchTradeFeed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Offer failed!');
    }
  };

  const handleRespondTrade = async (tradeId, action) => {
    try {
      const res = await apiRef.current.post('/pets/trade/respond', { trade_id: tradeId, action });
      toast.success(res.data.message);
      await fetchTrades();
      await fetchData();
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl animate-bounce mb-4">🐾</div>
          <p className="text-xl text-slate-600 font-fredoka">Loading Pet World...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pet-world-page" className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-pink-50">
      {/* Confirm Modal */}
      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          emoji={confirmAction.emoji}
          confirmText={confirmAction.confirmText}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="back-btn" onClick={() => navigate('/coin-world')} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <h1 className="text-lg font-bold text-slate-900 font-fredoka">Pet World</h1>
          </div>
          <div className="flex items-center gap-1 px-3 py-1 bg-amber-100 rounded-full">
            <Coins className="w-4 h-4 text-amber-600" />
            <span data-testid="coin-balance" className="font-bold text-sm text-amber-700">{data?.coins || 0}</span>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-2xl mx-auto px-4 pt-4">
        <div className="flex gap-1 bg-white/60 rounded-2xl p-1.5">
          {TABS.map(tab => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-1.5 transition-all ${
                activeTab === tab.id ? 'bg-white text-purple-700 shadow-md' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">

        {/* ── MY PETS TAB ── */}
        {activeTab === 'my_pets' && (
          <>
            {data?.my_pets?.length === 0 ? (
              <div className="text-center py-12 bg-white/60 rounded-3xl">
                <div className="text-6xl mb-4">🥺</div>
                <h3 className="text-xl font-bold text-slate-800 font-fredoka mb-2">No Pets Yet!</h3>
                <p className="text-slate-500 mb-4">Visit the Adoption Center to get your first pet!</p>
                <button onClick={() => setActiveTab('adopt')} className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-bold hover:shadow-lg transition-all">
                  <Plus className="w-5 h-5 inline mr-1" /> Adopt a Pet
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {data.my_pets.map(pet => (
                    <PetMiniCard key={pet.pet_id} pet={pet} onClick={setSelectedPet} isSelected={selectedPet?.pet_id === pet.pet_id} />
                  ))}
                  <button onClick={() => setActiveTab('adopt')} className="p-3 rounded-2xl border-2 border-dashed border-slate-300 text-slate-400 hover:border-purple-400 hover:text-purple-500 transition-all flex flex-col items-center justify-center">
                    <Plus className="w-8 h-8" />
                    <span className="text-xs font-bold mt-1">Adopt</span>
                  </button>
                </div>

                {selectedPet && (
                  <PetDetail
                    pet={selectedPet}
                    foodInventory={data.food_inventory}
                    foodTypes={data.food_types}
                    onFeed={handleFeed}
                    onClose={() => setSelectedPet(null)}
                    ownedAccessories={data.owned_accessories}
                    accessoriesStore={data.accessories_store}
                    onAccessorize={handleAccessorize}
                  />
                )}
              </>
            )}
          </>
        )}

        {/* ── ADOPT TAB ── */}
        {activeTab === 'adopt' && (
          <>
            {/* Common pets */}
            <div>
              <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                <span className="text-xl">🐾</span> Common Pets
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {data?.catalog?.common?.map(pet => (
                  <AdoptionCard key={pet.type} pet={pet} onAdopt={handleAdopt} userCoins={data?.coins || 0} />
                ))}
              </div>
            </div>
            {/* Uncommon pets */}
            {data?.catalog?.uncommon?.length > 0 && (
              <div>
                <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <span className="text-xl">🦊</span> Uncommon Pets
                  <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">+1-2 bonus coins!</span>
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {data?.catalog?.uncommon?.map(pet => (
                    <AdoptionCard key={pet.type} pet={pet} onAdopt={handleAdopt} userCoins={data?.coins || 0} />
                  ))}
                </div>
              </div>
            )}
            {/* Rare pets */}
            <div>
              <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                <span className="text-xl">✨</span> Rare Pets
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Bonus coins on activities!</span>
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {data?.catalog?.rare?.map(pet => (
                  <AdoptionCard key={pet.type} pet={pet} onAdopt={handleAdopt} userCoins={data?.coins || 0} />
                ))}
              </div>
            </div>
            {/* Info */}
            <div className="bg-white/70 rounded-2xl p-4 border border-purple-200 text-sm text-slate-600 space-y-1">
              <p className="font-bold text-slate-700 flex items-center gap-1"><Sparkles className="w-4 h-4 text-purple-500" /> How it works</p>
              <p>Adopt pets with Brain Coins. Feed them to increase happiness and growth.</p>
              <p>Rare pets give you <strong>bonus coins</strong> every time you complete an activity!</p>
              <p>Pets grow: Baby &rarr; Kid &rarr; Teen &rarr; Super Pet (7 growth XP per stage)</p>
            </div>
          </>
        )}

        {/* ── SHOP TAB ── */}
        {activeTab === 'shop' && (
          <>
            {!selectedPet && data?.my_pets?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center">
                <p className="text-amber-700 font-bold text-sm">Select a pet from "My Pets" tab first to equip accessories!</p>
              </div>
            )}
            {selectedPet && (
              <div className="bg-purple-50 rounded-2xl p-3 flex items-center gap-3 border border-purple-200">
                <span className="text-3xl">{selectedPet.emoji}</span>
                <div>
                  <p className="font-bold text-purple-800 text-sm">Shopping for: {selectedPet.name}</p>
                  <p className="text-purple-500 text-xs">Tap an accessory to buy & equip</p>
                </div>
              </div>
            )}
            {Object.entries(data?.accessories_store || {}).map(([cat, items]) => (
              <div key={cat}>
                <h4 className="font-bold text-slate-700 capitalize mb-2 text-sm">{cat}</h4>
                <div className="grid grid-cols-3 gap-2">
                  {items.map(item => (
                    <AccessoryItem
                      key={item.id}
                      item={item}
                      onBuy={handleAccessorize}
                      selectedPetId={selectedPet?.pet_id}
                      userCoins={data?.coins || 0}
                    />
                  ))}
                </div>
              </div>
            ))}
            {/* Food Inventory */}
            <div>
              <h4 className="font-bold text-slate-700 mb-2 text-sm flex items-center gap-1"><Utensils className="w-4 h-4" /> My Food Stash</h4>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(data?.food_types || {}).map(([key, info]) => (
                  <div key={key} className="bg-white rounded-xl p-3 border border-slate-200 text-center">
                    <span className="text-2xl">{info.emoji}</span>
                    <p className="text-xs font-bold text-slate-700 mt-1">{info.name}</p>
                    <p className="text-lg font-black text-slate-900">{data?.food_inventory?.[key] || 0}</p>
                    <p className="text-[10px] text-slate-400">+{info.happiness} happy, +{info.growth} XP</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-2 text-center">Earn food by completing quizzes & riddles!</p>
            </div>
          </>
        )}

        {/* ── TRADE TAB ── */}
        {activeTab === 'trade' && (
          <>
            {/* Trade limits */}
            <div className="bg-white rounded-2xl p-4 border border-slate-200 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Trades left today:</span>
                <span className="font-bold text-slate-900">{tradeData?.trades_remaining ?? 2}/2</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-slate-600">Rare trades left:</span>
                <span className="font-bold text-amber-600">{tradeData?.rare_trades_remaining ?? 1}/1</span>
              </div>
            </div>

            {data?.my_pets?.length === 0 ? (
              <div className="text-center py-12 bg-white/60 rounded-3xl">
                <div className="text-5xl mb-3">🔄</div>
                <p className="text-slate-500">You need pets to trade! Adopt some first.</p>
              </div>
            ) : (
              <>
                {/* Swap Picker Overlay */}
                {swapPet && (
                  <div data-testid="swap-picker" className="bg-white rounded-2xl p-4 border-2 border-purple-300 shadow-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
                        <ArrowRightLeft className="w-4 h-4" /> Swap {swapPet.name}
                      </h3>
                      <button data-testid="cancel-swap-btn" onClick={() => { setSwapPet(null); setSwapTarget(null); }}
                        className="p-1 hover:bg-slate-100 rounded-full">
                        <X className="w-5 h-5 text-slate-400" />
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mb-3">Pick the pet you want, or leave empty for a random swap:</p>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {[...(data?.catalog?.common || []), ...(data?.catalog?.uncommon || []), ...(data?.catalog?.rare || [])].filter(
                        p => p.type !== swapPet.pet_type && !data.my_pets.some(mp => mp.pet_type === p.type)
                      ).map(pet => (
                        <button key={pet.type} data-testid={`swap-pick-${pet.type}`}
                          onClick={() => setSwapTarget(pet)}
                          className={`p-2 rounded-xl border-2 text-center transition-all ${
                            swapTarget?.type === pet.type ? 'border-purple-500 bg-purple-50 shadow-md' : 'border-slate-200 bg-slate-50 hover:border-purple-300'
                          } ${pet.rarity === 'rare' ? 'ring-1 ring-amber-300' : ''}`}
                        >
                          <span className="text-2xl block">{pet.emoji}</span>
                          <span className="text-[10px] font-bold text-slate-700 truncate block">{pet.name}</span>
                          {pet.rarity === 'rare' && <span className="text-[8px] text-amber-600 font-bold">Rare</span>}
                        </button>
                      ))}
                    </div>
                    {swapPet.rarity === 'rare' && swapTarget && swapTarget.rarity !== 'rare' && (
                      <div data-testid="fair-trade-warning" className="flex items-center gap-2 bg-amber-50 border border-amber-300 rounded-xl p-2 mb-3">
                        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                        <p className="text-xs text-amber-700 font-medium">Warning: Trading a Rare pet for a Common pet!</p>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button onClick={() => { setSwapPet(null); setSwapTarget(null); }}
                        className="flex-1 py-2.5 bg-slate-100 rounded-xl font-bold text-sm text-slate-600">Cancel</button>
                      <button data-testid="confirm-swap-btn" onClick={confirmSwap}
                        className="flex-1 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-bold text-sm">
                        {swapTarget ? `Swap for ${swapTarget.name}` : 'Random Swap'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Quick Actions: Trade for Coins / Swap */}
                {!swapPet && (
                  <div>
                    <h3 className="font-bold text-slate-800 text-sm mb-2 flex items-center gap-2">
                      <Coins className="w-4 h-4" /> Quick Trade
                    </h3>
                    <div className="space-y-2">
                      {data.my_pets.map(pet => (
                        <div key={pet.pet_id} data-testid={`trade-${pet.pet_id}`} className="bg-white rounded-2xl border-2 border-slate-200 p-3">
                          <div className="flex items-center gap-3">
                            <span className="text-3xl">{pet.emoji}</span>
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-slate-800 text-sm truncate">{pet.name}</p>
                              <p className="text-[10px] text-slate-500">{GROWTH_LABELS[pet.growth_stage]} {pet.rarity === 'rare' ? '(Rare)' : ''}</p>
                            </div>
                            <button data-testid={`trade-coins-${pet.pet_id}`} onClick={() => handleTrade(pet.pet_id, 'coins')}
                              className="px-3 py-2 bg-amber-100 text-amber-700 rounded-xl font-bold text-xs hover:bg-amber-200 transition-all flex items-center gap-1">
                              <Coins className="w-3 h-3" /> Coins
                            </button>
                            <button data-testid={`trade-random-${pet.pet_id}`} onClick={() => handleTrade(pet.pet_id, 'random')}
                              className="px-3 py-2 bg-purple-100 text-purple-700 rounded-xl font-bold text-xs hover:bg-purple-200 transition-all flex items-center gap-1">
                              <ArrowRightLeft className="w-3 h-3" /> Swap
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Trade with Friends */}
                <div className="bg-white rounded-2xl p-4 border border-slate-200">
                  <h3 className="font-bold text-slate-800 text-sm mb-3 flex items-center gap-2">
                    <Send className="w-4 h-4" /> Trade with a Friend
                  </h3>

                  {tradePet ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 bg-purple-50 rounded-xl p-3">
                        <span className="text-3xl">{tradePet.emoji}</span>
                        <div className="flex-1">
                          <p className="font-bold text-slate-800 text-sm">Offering: {tradePet.name}</p>
                          <p className="text-[10px] text-slate-500">{GROWTH_LABELS[tradePet.growth_stage]}</p>
                        </div>
                        <button onClick={() => { setTradePet(null); setWantPetType(null); }} className="p-1 hover:bg-purple-100 rounded-full">
                          <X className="w-4 h-4 text-slate-400" />
                        </button>
                      </div>
                      <input data-testid="trade-username-input" value={tradeUsername} onChange={e => setTradeUsername(e.target.value)}
                        placeholder="Friend's username (empty = random)"
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:border-purple-400" />

                      {/* Visual pet picker for what they want */}
                      <div>
                        <p className="text-xs text-slate-500 mb-2">What do you want in return? (optional)</p>
                        <div className="grid grid-cols-4 gap-1.5">
                          <button data-testid="want-any-pet" onClick={() => setWantPetType(null)}
                            className={`p-2 rounded-lg border text-center text-xs transition-all ${!wantPetType ? 'border-purple-500 bg-purple-50 font-bold' : 'border-slate-200'}`}>
                            Any
                          </button>
                          {[...(data?.catalog?.common || []), ...(data?.catalog?.uncommon || []), ...(data?.catalog?.rare || [])].map(p => (
                            <button key={p.type} data-testid={`want-pet-${p.type}`} onClick={() => setWantPetType(p.type)}
                              className={`p-1.5 rounded-lg border text-center transition-all ${
                                wantPetType === p.type ? 'border-purple-500 bg-purple-50 shadow' : 'border-slate-200 hover:border-purple-200'
                              }`}>
                              <span className="text-lg block">{p.emoji}</span>
                              <span className="text-[8px] text-slate-600 truncate block">{p.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {tradePet.rarity === 'rare' && wantPetType && !['crystal_cub', 'galaxy_octopus', 'golden_dino', 'lightning_dragon'].includes(wantPetType) && (
                        <div data-testid="friend-fair-trade-warning" className="flex items-center gap-2 bg-amber-50 border border-amber-300 rounded-xl p-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                          <p className="text-xs text-amber-700 font-medium">Warning: Trading a Rare pet for a Common one!</p>
                        </div>
                      )}

                      <button data-testid="send-pet-trade-btn" onClick={handleSendFriendTrade}
                        className="w-full py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:shadow-md transition-all">
                        <Send className="w-4 h-4" /> {tradeUsername ? 'Send to Friend' : 'Send to Random'}
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p className="text-slate-500 text-sm mb-3">Pick a pet to offer:</p>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {data.my_pets.map(pet => (
                          <button key={pet.pet_id} data-testid={`friend-trade-pick-${pet.pet_id}`} onClick={() => setTradePet(pet)}
                            className="p-2 bg-slate-50 rounded-xl border border-slate-200 hover:border-purple-300 text-center transition-all">
                            <span className="text-2xl block">{pet.emoji}</span>
                            <span className="text-[10px] text-slate-600 font-bold truncate block">{pet.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Incoming Trades */}
                {tradeData?.incoming?.length > 0 && (
                  <div>
                    <h3 className="font-bold text-slate-800 text-sm mb-2 flex items-center gap-2">
                      <Package className="w-4 h-4" /> Incoming Trades ({tradeData.incoming.length})
                    </h3>
                    <div className="space-y-2">
                      {tradeData.incoming.map(t => (
                        <div key={t.trade_id} data-testid={`incoming-pet-trade-${t.trade_id}`}
                          className="bg-white rounded-2xl p-3 border border-slate-200 flex items-center gap-3">
                          <span className="text-3xl">{t.offer_pet_emoji}</span>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-slate-800 text-sm">{t.from_username} offers {t.offer_pet_name}</p>
                            <p className="text-[10px] text-slate-500">{t.offer_pet_rarity === 'rare' ? 'Rare' : 'Common'} pet</p>
                            {t.want_pet_type && (
                              <p className="text-[10px] text-indigo-600">Wants: {t.want_pet_type}</p>
                            )}
                          </div>
                          <div className="flex gap-1.5 flex-shrink-0">
                            <button data-testid={`accept-pet-${t.trade_id}`} onClick={() => handleRespondTrade(t.trade_id, 'accept')}
                              className="px-3 py-2 bg-green-500 text-white rounded-xl text-xs font-bold">Accept</button>
                            <button data-testid={`decline-pet-${t.trade_id}`} onClick={() => handleRespondTrade(t.trade_id, 'decline')}
                              className="px-3 py-2 bg-slate-200 text-slate-600 rounded-xl text-xs font-bold">No</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Outgoing Trades with Cancel */}
                {tradeData?.outgoing?.length > 0 && (
                  <div>
                    <h3 className="font-bold text-slate-800 text-sm mb-2">Pending Sent Trades</h3>
                    <div className="space-y-2">
                      {tradeData.outgoing.map(t => (
                        <div key={t.trade_id} data-testid={`outgoing-pet-trade-${t.trade_id}`} className="bg-slate-50 rounded-xl p-3 flex items-center gap-3 text-sm">
                          <span className="text-2xl">{t.offer_pet_emoji}</span>
                          <div className="flex-1">
                            <p className="text-slate-700">Offering {t.offer_pet_name} to <strong>{t.to_username}</strong></p>
                            {t.want_pet_type && <p className="text-[10px] text-indigo-600">Wants: {t.want_pet_type}</p>}
                          </div>
                          <button data-testid={`cancel-pet-trade-${t.trade_id}`} onClick={() => handleCancelPetTrade(t.trade_id)}
                            className="px-3 py-1.5 bg-red-100 text-red-600 rounded-lg text-xs font-bold hover:bg-red-200 transition-colors flex items-center gap-1">
                            <XCircle className="w-3 h-3" /> Cancel
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Trading Rules */}
            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 text-xs text-slate-500 space-y-1">
              <p className="font-bold text-slate-700 text-sm mb-1">Trading Rules</p>
              <p>- <strong>Trade for Coins:</strong> Get 60% of the adoption cost back</p>
              <p>- <strong>Swap Pet:</strong> Pick a specific pet or get a random one</p>
              <p>- <strong>Friend Trade:</strong> Send a pet to a friend and get one of theirs back</p>
              <p>- You can make <strong>2 trades per day</strong>, only <strong>1 rare pet trade</strong></p>
              <p>- A <strong className="text-amber-600">warning</strong> appears when trading Rare for Common</p>
            </div>
          </>
        )}

        {/* ── TRADING POST TAB ── */}
        {activeTab === 'trading_post' && (
          <>
            <div>
              <h3 className="font-bold text-slate-800 text-sm mb-2 flex items-center gap-2">
                <Globe className="w-4 h-4" /> Active Pet Trades
              </h3>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input data-testid="pet-feed-search" value={feedSearch}
                  onChange={e => { setFeedSearch(e.target.value); fetchTradeFeed(e.target.value); }}
                  placeholder="Search for a pet you want..."
                  className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:border-purple-400" />
              </div>

              {tradeFeed?.trades?.length > 0 ? (
                <div className="space-y-2">
                  {tradeFeed.trades.map(t => (
                    <div key={t.trade_id} data-testid={`feed-pet-${t.trade_id}`}
                      className={`bg-white rounded-2xl p-3 border border-slate-200 transition-all ${
                        t.offer_pet_rarity === 'rare' ? 'ring-1 ring-amber-300 shadow-[0_0_8px_rgba(251,191,36,0.3)]' : ''
                      }`}>
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{t.offer_pet_emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-slate-800 text-sm">{t.from_username} offers {t.offer_pet_name}</p>
                          <div className="flex items-center gap-2 text-[10px]">
                            {t.offer_pet_rarity === 'rare' && <span className="text-amber-600 font-bold">Rare</span>}
                            <span className="text-slate-400">to {t.to_username}</span>
                          </div>
                          {t.want_pet_type && <p className="text-[10px] text-indigo-600">Wants: {t.want_pet_type}</p>}
                        </div>
                        {data?.my_pets?.length > 0 && (
                          <button data-testid={`offer-btn-${t.trade_id}`}
                            onClick={() => { setOfferTradeId(t.trade_id); setOfferPetPick(null); }}
                            className="px-3 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl text-xs font-bold hover:shadow-md transition-all flex items-center gap-1">
                            <Send className="w-3 h-3" /> Offer Trade
                          </button>
                        )}
                      </div>

                      {/* Offer Trade picker */}
                      {offerTradeId === t.trade_id && (
                        <div data-testid={`offer-picker-${t.trade_id}`} className="mt-3 pt-3 border-t border-slate-200">
                          <p className="text-xs text-slate-500 mb-2">Pick your pet to offer:</p>
                          <div className="grid grid-cols-4 gap-1.5 mb-2">
                            {data.my_pets.map(pet => (
                              <button key={pet.pet_id} data-testid={`offer-pick-${pet.pet_id}`}
                                onClick={() => setOfferPetPick(pet)}
                                className={`p-1.5 rounded-lg border text-center transition-all ${
                                  offerPetPick?.pet_id === pet.pet_id ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-purple-300'
                                }`}>
                                <span className="text-xl block">{pet.emoji}</span>
                                <span className="text-[8px] text-slate-600 truncate block">{pet.name}</span>
                              </button>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => { setOfferTradeId(null); setOfferPetPick(null); }}
                              className="flex-1 py-2 bg-slate-100 rounded-xl text-xs font-bold text-slate-600">Cancel</button>
                            <button data-testid={`confirm-offer-${t.trade_id}`}
                              disabled={!offerPetPick}
                              onClick={() => handleOfferOnTrade(t.trade_id, offerPetPick.pet_id)}
                              className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all ${
                                offerPetPick ? 'bg-gradient-to-r from-green-400 to-emerald-500 text-white' : 'bg-slate-200 text-slate-400'
                              }`}>
                              Send Offer
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10 bg-white/60 rounded-3xl">
                  <span className="text-5xl block mb-3">🔍</span>
                  <p className="text-slate-400 text-sm">{feedSearch ? 'No trades found for that pet' : 'No active trades right now'}</p>
                  <p className="text-slate-400 text-xs mt-1">Send a trade from the Trade tab to get started!</p>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default PetWorldPage;
